import { z } from 'zod';
import { APLICACION_ESTADO, AJUSTE_SCOPE_TIPO, COBRO_GENERADO_POR, INVOICE_TYPE } from './types';

const moneyRegex = /^\d+(\.\d{1,2})?$/;
const isoDateRegex = /^\d{4}-\d{2}-\d{2}$/;
const isoDateTimeRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{1,3})?(Z|[+-]\d{2}:\d{2})?$/;

const CreateCobroDtoBaseSchema = z.object({
  idCliente: z.coerce.number().int(),
  tipoFactura: z
    .enum([INVOICE_TYPE.PERIODICA, INVOICE_TYPE.EXTRAORDINARIA])
    .default(INVOICE_TYPE.PERIODICA),
  fechaInicio: z
    .string()
    .regex(isoDateRegex, 'fechaInicio must be YYYY-MM-DD')
    .nullable()
    .optional(),
  fechaFin: z.string().regex(isoDateRegex, 'fechaFin must be YYYY-MM-DD').nullable().optional(),
  fechaCobro: z.string().regex(isoDateRegex, 'fechaCobro must be YYYY-MM-DD'),
  valor: z.string().regex(moneyRegex, 'valor must be a valid money value (max 2 decimals)'),
  concepto: z.string().trim().min(1).optional(),
  /** Deprecated input alias; `concepto` is the canonical field. */
  motivo: z.string().trim().min(1).optional(),
  comentario: z.string().optional(),
  /** Defaults to the legacy behavior: consume available client credit. */
  aplicarSaldoAFavor: z.boolean().default(true),
  /** Optional caller-owned key for safe retries and invoice replacement. */
  operacionKey: z.string().trim().min(1).optional(),
});

export const CreateCobroDtoSchema = CreateCobroDtoBaseSchema.superRefine((value, context) => {
  if (value.tipoFactura === INVOICE_TYPE.PERIODICA) {
    if (!value.fechaInicio) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['fechaInicio'],
        message: 'fechaInicio is required for PERIODICA invoices',
      });
    }
    if (!value.fechaFin) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['fechaFin'],
        message: 'fechaFin is required for PERIODICA invoices',
      });
    }
    if (value.fechaInicio && value.fechaCobro !== value.fechaInicio) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['fechaCobro'],
        message: 'fechaCobro must equal fechaInicio for PERIODICA invoices',
      });
    }
    return;
  }

  if (value.fechaInicio !== undefined && value.fechaInicio !== null) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['fechaInicio'],
      message: 'fechaInicio must be empty for EXTRAORDINARIA invoices',
    });
  }
  if (value.fechaFin !== undefined && value.fechaFin !== null) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['fechaFin'],
      message: 'fechaFin must be empty for EXTRAORDINARIA invoices',
    });
  }
  if (!value.concepto && !value.motivo) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['concepto'],
      message: 'concepto is required for EXTRAORDINARIA invoices',
    });
  }
});

/** Input shape keeps legacy callers compatible; parsing applies the defaults. */
export type CreateCobroDto = z.input<typeof CreateCobroDtoSchema>;

export const UpdateCobroDtoSchema = z.object({
  fechaInicio: z.string().regex(isoDateRegex).optional(),
  fechaFin: z.string().regex(isoDateRegex).optional(),
  fechaCobro: z.string().regex(isoDateRegex).optional(),
  valor: z.string().regex(moneyRegex).optional(),
  comentario: z.string().optional(),
});

export type UpdateCobroDto = z.infer<typeof UpdateCobroDtoSchema>;

export const AnularCobroDtoSchema = z.object({
  motivo: z.string().min(1),
});

export type AnularCobroDto = z.infer<typeof AnularCobroDtoSchema>;

export const GenerarCobrosDtoSchema = z.object({
  fechaCobro: z.string().regex(isoDateRegex, 'fechaCobro must be YYYY-MM-DD'),
});

export type GenerarCobrosDto = z.infer<typeof GenerarCobrosDtoSchema>;

export const QueryCobrosDtoSchema = z.object({
  offset: z.coerce.number().int().min(0).default(0),
  limit: z.coerce.number().int().min(1).max(100).default(20),
  search: z.string().trim().max(120).optional(),
  tipoFactura: z.enum([INVOICE_TYPE.PERIODICA, INVOICE_TYPE.EXTRAORDINARIA]).optional(),
  idCliente: z.coerce.number().int().optional(),
  idEstado: z.coerce.number().int().optional(),
  fechaDesde: z.string().regex(isoDateRegex).optional(),
  fechaHasta: z.string().regex(isoDateRegex).optional(),
});

export type QueryCobrosDto = z.infer<typeof QueryCobrosDtoSchema>;

/**
 * Spec: cobro_excepcion is restricted to invoice-scope or period-scope.
 * Either idCobro or periodoInicio (with idCliente) MUST be set. The backend
 * rejects unscoped submissions (422). The canonical DTO lives in
 * `../excepciones/dto` and is re-exported via the package index. Re-export
 * here for convenience inside the cobros module:
 *
 * Note (billing-domain-rules Blocker 2 — workspace build): `CreateExcepcionDto`
 * is a TYPE-only alias in `excepciones/dto.ts`. Re-exporting it as a value
 * here made Vite/Rollup reject the workspace build (the `.d.ts` claimed a
 * runtime export that did not exist). Splitting the export statement keeps the
 * schema available as a runtime value while the type re-export stays type-only.
 */
export { CreateExcepcionDtoSchema } from '../excepciones/dto';
export type { CreateExcepcionDto } from '../excepciones/dto';

/**
 * Acquire a prepaid package — captures gross, manual discount, net, periods,
 * and the immutable service/price snapshot.
 */
export const AcquirePaqueteDtoSchema = z.object({
  idCliente: z.coerce.number().int(),
  idPago: z.coerce.number().int(),
  bruto: z.string().regex(moneyRegex),
  descuentoManual: z.string().regex(moneyRegex),
  neto: z.string().regex(moneyRegex),
  periodos: z
    .array(
      z.object({
        inicio: z.string().regex(isoDateRegex),
        fin: z.string().regex(isoDateRegex),
      }),
    )
    .min(1),
  lineas: z
    .array(
      z.object({
        idServicio: z.coerce.number().int(),
        idPlan: z.coerce.number().int(),
        descripcion: z.string(),
        precioUnitario: z.string().regex(moneyRegex),
        subtotal: z.string().regex(moneyRegex),
      }),
    )
    .min(1),
});

export type AcquirePaqueteDto = z.infer<typeof AcquirePaqueteDtoSchema>;

/**
 * Materialise a single prepaid period into a monthly invoice.
 * Backend requires the periodoInicio to be covered by the paquete.
 */
export const MaterialisePeriodoDtoSchema = z.object({
  idPaquete: z.coerce.number().int(),
  periodoInicio: z.string().regex(isoDateRegex),
});

export type MaterialisePeriodoDto = z.infer<typeof MaterialisePeriodoDtoSchema>;

export {
  APLICACION_ESTADO,
  AJUSTE_SCOPE_TIPO,
  COBRO_GENERADO_POR,
  INVOICE_TYPE,
  TIPO_FACTURA,
} from './types';
